/**
 * The only module that fetches the jukebox's shelf (SPEC-014).
 *
 * `soundtrack.json` is written by `npm run assets:soundtrack` and served as a
 * static asset beside the tracks it names. It is fetched rather than bundled for
 * the reason the catalogue is: eight titles and eight durations are nothing on
 * the wire, but they are also nothing anybody needs before the sheet is opened,
 * and the app's own vocabulary has no business carrying them.
 *
 * THE AUDIO ITSELF IS NEVER FETCHED HERE, and that is the whole seam. A track is
 * three to seven megabytes and is played by an `<audio>` element, which streams
 * it with range requests, starts on the first few kilobytes and never holds the
 * file in memory. Pulling one through `fetch` to hand to WebAudio — which is
 * what `soundClient.js` does for the duel's ticks, correctly, because a tick is
 * seven kilobytes and has to fire on an exact frame — would download twenty-four
 * minutes of music into a phone's heap to play three of it.
 *
 * Its own timeout and retry rather than a shared helper, as
 * `deployedVersionClient` has its own: this repo's outbound modules each state
 * their rule where a reader of that module can see it. And they matter less here
 * than almost anywhere: a shelf that never arrives is a jukebox with nothing on
 * it, which is a state the sheet is built to draw.
 */

const SOUNDTRACK_MANIFEST_URL = '/assets/soundtrack/soundtrack.json';
const REQUEST_TIMEOUT_MS = 10000;

async function fetchManifestOnce(url, { fetchImpl = globalThis.fetch, timeoutMs = REQUEST_TIMEOUT_MS } = {}) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);

    try {
        const response = await fetchImpl(url, { signal: controller.signal });

        if (!response.ok) {
            throw new Error(`${url} responded ${response.status}`);
        }

        return await response.json();
    } finally {
        clearTimeout(timer);
    }
}

/**
 * The manifest, or a rejection.
 *
 * What comes back is NOT checked for being a shelf here — `readSoundtrack` in
 * the domain does that, and it has to anyway: hosting answers a missing asset
 * with the app shell and a 200, so a payload that parsed is not evidence of
 * anything. This module's job ends at "the bytes arrived and were JSON".
 */
export async function fetchSoundtrack(options = {}) {
    try {
        return await fetchManifestOnce(SOUNDTRACK_MANIFEST_URL, options);
    } catch (error) {
        // Sequential, and written out rather than looped, for the reason
        // `carContentClient` gives: a retry only means anything once the first
        // attempt has actually failed.
        return fetchManifestOnce(SOUNDTRACK_MANIFEST_URL, options);
    }
}

export { SOUNDTRACK_MANIFEST_URL };
